import { captureException } from '@/lib/errorTracking';
import { createFileRoute } from '@tanstack/react-router';
import { useRuntimeAuthSdk } from '@/auth/runtimeAuth';
import { useEffect, useId, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { ThemePicker } from '@/components/preferences/ThemePicker';
import { LanguagePicker } from '@/components/preferences/LanguagePicker';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';
import { TeamManagement } from './-components/TeamManagement';
import { WorkspaceSwitcher } from './-components/WorkspaceSwitcher';
import { type CheckoutHint, UsagePanel } from './-components/UsagePanel';
import {
  handleGetSubscriptionSubscriptionGetOptions,
  handleGetUsageUsageGetOptions,
} from '@/api/generated/@tanstack/react-query.gen';

function AccountPage() {
  const { user, profile, isIdentityPending, canEndSession, logout } = useAuth();
  // Stripe's return hint, validated by the route. Passed down rather than read
  // inside the panel so the "hint, never truth" boundary is visible here.
  const { checkout } = Route.useSearch();
  const navigate = Route.useNavigate();
  const authSdk = useRuntimeAuthSdk();
  const posthog = usePostHog();
  const { t } = useTranslation();
  // `useId`, not literals, to match every other association in this file's
  // neighbourhood — a literal id is only safe while the form renders once.
  const firstNameId = useId();
  const lastNameId = useId();
  // Seeded from `profile`, not `user`: it is populated on first paint, so the
  // form fills immediately instead of seeding `''` and then overwriting
  // whatever the user typed when `/auth/current` lands.
  const [firstName, setFirstName] = useState(() => profile.firstName ?? '');
  const [lastName, setLastName] = useState(() => profile.lastName ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Re-sync after a successful `updateProfile` (the only thing that moves these).
  useEffect(() => {
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
  }, [profile.firstName, profile.lastName]);

  const dirty =
    firstName !== (profile.firstName ?? '') ||
    lastName !== (profile.lastName ?? '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await authSdk.updateProfile(firstName, lastName);
      posthog.capture('profile_updated');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      logger.error('Failed to update name', { error: err });
      captureException(err, { action: 'profile_update' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex-1 overflow-y-auto bg-muted p-8">
      <div className="mx-auto max-w-4xl">
        <h1 className="text-lg font-semibold text-foreground">
          {t('account.title')}
        </h1>

        {/* Profile section */}
        <Card className="mt-6 p-5">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t('account.profile')}
          </h2>

          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor={firstNameId}
                  className="text-xs text-muted-foreground"
                >
                  {t('account.firstName')}
                </label>
                <input
                  id={firstNameId}
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-0.5 w-full rounded border border-border bg-card px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-ring"
                />
              </div>
              <div>
                <label
                  htmlFor={lastNameId}
                  className="text-xs text-muted-foreground"
                >
                  {t('account.lastName')}
                </label>
                <input
                  id={lastNameId}
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-0.5 w-full rounded border border-border bg-card px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-ring"
                />
              </div>
            </div>

            {/*
              Email is a read-only value, not a control, so this is a
              term/definition pair rather than a form label (ENG-1460). A
              <label> over a <p> is announced as labelling nothing, and it
              hides the real unlabelled-field population.
            */}
            <dl>
              <dt className="text-xs text-muted-foreground">
                {t('account.email')}
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {profile.email ?? '—'}
              </dd>
            </dl>
          </div>

          {dirty && (
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="mt-4 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? t('account.saving') : t('account.save')}
            </button>
          )}
          {saved && (
            <p className="mt-2 text-xs text-success">{t('account.saved')}</p>
          )}
        </Card>

        <WorkspaceSwitcher />

        <TeamManagement
          organization={user?.organization ?? null}
          identityPending={isIdentityPending}
          currentUserId={user?.id ?? ''}
        />

        <UsagePanel
          checkout={checkout}
          // Dropping the hint is a navigation, so the route owns it and the
          // panel stays a component that is handed a hint. `replace`, not a
          // push: the hint arrives on Stripe's redirect, and a pushed
          // replacement would leave the banner's own entry in history — Back
          // would walk straight back into it, which is the bug this fixes.
          onDismissCheckout={() =>
            void navigate({ to: '/account', search: {}, replace: true })
          }
        />

        {/* Theme section */}
        <Card className="mt-6 p-5">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t('account.theme')}
          </h2>
          <ThemePicker capture />
        </Card>

        {/* Language section */}
        <Card className="mt-6 p-5">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t('account.language')}
          </h2>
          <LanguagePicker capture />
        </Card>

        <Card className="mt-6 flex items-center justify-between gap-4 p-5">
          <div>
            <h2 className="text-sm font-medium text-foreground">
              {t('account.signOut')}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {canEndSession
                ? t('account.signOutDescription')
                : t('nav.logoutUnavailable')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!canEndSession}
            onClick={() => void logout()}
          >
            {t('account.signOut')}
          </Button>
        </Card>

        {/* User ID — shown for support/debugging */}
        {user?.id && (
          <p className="mt-6 text-center text-2xs text-muted-foreground">
            {t('account.userId')}: {user.id}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * What Stripe appended when it returned the buyer here, narrowed to the two
 * values this page renders.
 *
 * Anything else — including the `session_id` that rides along for support —
 * is dropped rather than passed through. The hint only chooses a banner; what
 * the organization actually owns is read from `GET /subscription`, so an
 * invented or replayed query string cannot make the page claim a plan
 * (`docs/designs/platform/stripe-webhooks.md` §Only the webhook writes billing
 * state).
 */
function validateCheckoutSearch(search: Record<string, unknown>): {
  checkout?: CheckoutHint;
} {
  const value = search.checkout;
  return value === 'success' || value === 'cancelled'
    ? { checkout: value }
    : {};
}

export const Route = createFileRoute('/_app/account/')({
  validateSearch: validateCheckoutSearch,
  // Warm the two billing reads on hover (`defaultPreload: 'intent'`). The app
  // shell used to warm `/subscription` on every mount for nobody (ENG-1141
  // removed that); the read belongs to the one route that renders it, started
  // as early as that route is known.
  //
  // **Started, never awaited** — unlike the product-library loaders, which have
  // one query each and nothing else on the page. This route's billing panel is
  // one of five cards, so awaiting it would put profile, team and sign-out
  // behind it: `.catch()` swallows the final error but not the wait, and
  // `retryUnlessSessionExpired`'s three attempts turn a 5xx or a network
  // timeout on `/usage` into several seconds of full-page loading instead of
  // the panel's own `unread` state. The panel's `useQuery` adopts whatever this
  // has in flight, so the warm still pays off.
  loader: ({ context: { queryClient } }) => {
    void queryClient
      .ensureQueryData(handleGetSubscriptionSubscriptionGetOptions())
      .catch(() => undefined);
    void queryClient
      .ensureQueryData(handleGetUsageUsageGetOptions())
      .catch(() => undefined);
  },
  component: AccountPage,
});
